import { ModalForm, ProFormText } from "@ant-design/pro-components";
import { message } from "antd";
import React from "react";
import { useIntl, history, useModel } from "@umijs/max";
import { updatePassword } from "@/services/system";
import { withGoogle } from "@/components/WithGoogle";
import { LockOutlined } from "@ant-design/icons";
import { useUserStore } from "@/stores/useUserStore";

interface ChangePasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  googleTicket?: string;
}

const ChangePasswordModalContent: React.FC<ChangePasswordModalProps> = (
  props,
) => {
  const { open, onOpenChange, googleTicket } = props;
  const intl = useIntl();
  const { logout } = useUserStore();
  const { setInitialState } = useModel("@@initialState");

  return (
    <ModalForm
      title={intl.formatMessage({ id: "menu.account.change_password" })}
      open={open}
      onOpenChange={onOpenChange}
      modalProps={{
        destroyOnHidden: true,
        maskClosable: false,
      }}
      width={400}
      onFinish={async (values) => {
        if (!googleTicket) {
          message.error(
            intl.formatMessage({ id: "component.google.noTicket" }),
          );
          return false;
        }

        try {
          await updatePassword({
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
            verifyToken: googleTicket,
          });
          message.success(
            intl.formatMessage({
              id: "pages.system.manager.message.passwordSuccess",
            }),
          );

          // 修改成功后退出登录
          setInitialState((s) => ({ ...s, currentUser: undefined }));
          logout();
          localStorage.removeItem("token");
          history.push("/login");
          return true;
        } catch (error: any) {
          return false;
        }
      }}
    >
      <ProFormText.Password
        name="oldPassword"
        label={intl.formatMessage({
          id: "pages.system.manager.form.oldPassword",
        })}
        placeholder={intl.formatMessage({
          id: "pages.system.manager.form.oldPassword.required",
        })}
        rules={[
          {
            required: true,
            message: intl.formatMessage({
              id: "pages.system.manager.form.oldPassword.required",
            }),
          },
          {
            min: 6,
            message: intl.formatMessage({
              id: "pages.system.manager.form.newPassword.min",
            }),
          },
          {
            pattern: /^\S*$/,
            message: intl.formatMessage({
              id: "pages.system.manager.form.newPassword.noSpace",
            }),
          },
          {
            pattern: /^[^\u4e00-\u9fa5]*$/,
            message: intl.formatMessage({
              id: "pages.common.error.noChinese",
            }),
          },
        ]}
        fieldProps={{
          prefix: <LockOutlined />,
        }}
      />
      <ProFormText.Password
        name="newPassword"
        label={intl.formatMessage({
          id: "pages.system.manager.form.newPassword",
        })}
        placeholder={intl.formatMessage({
          id: "pages.system.manager.form.newPassword.required",
        })}
        rules={[
          {
            required: true,
            message: intl.formatMessage({
              id: "pages.system.manager.form.newPassword.required",
            }),
          },
          {
            min: 6,
            message: intl.formatMessage({
              id: "pages.system.manager.form.newPassword.min",
            }),
          },
          {
            pattern: /^\S*$/,
            message: intl.formatMessage({
              id: "pages.system.manager.form.newPassword.noSpace",
            }),
          },
          {
            pattern: /^[^\u4e00-\u9fa5]*$/,
            message: intl.formatMessage({
              id: "pages.common.error.noChinese",
            }),
          },
        ]}
        fieldProps={{
          prefix: <LockOutlined />,
        }}
      />
      <ProFormText.Password
        name="reNewPassword"
        label={intl.formatMessage({
          id: "pages.system.manager.form.confirmPassword",
        })}
        placeholder={intl.formatMessage({
          id: "pages.system.manager.form.confirmPassword.required",
        })}
        dependencies={["newPassword"]}
        rules={[
          {
            required: true,
            message: intl.formatMessage({
              id: "pages.system.manager.form.confirmPassword.required",
            }),
          },
          {
            pattern: /^[^\u4e00-\u9fa5]*$/,
            message: intl.formatMessage({
              id: "pages.common.error.noChinese",
            }),
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("newPassword") === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: "pages.system.manager.form.confirmPassword.mismatch",
                  }),
                ),
              );
            },
          }),
        ]}
        fieldProps={{
          prefix: <LockOutlined />,
        }}
      />
    </ModalForm>
  );
};

export const ChangePasswordModal = withGoogle(ChangePasswordModalContent);
